import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, TextInput } from 'grommet';
import { DataTableHeader, DataTableItem, Link, LoadingSpinner, Text } from '/src/components';
import { SalesCommission } from '/src/lib/models';
import { getPeriodFromDateString } from '/src/utils';

interface EditableSalesCommission extends SalesCommission {
  isEditing?: boolean;
  editedPercentRate?: string;
}

export type SalesCommissionsListProps = {
  salesCommissions?: SalesCommission[];
  isYearly?: boolean;
};

export const SalesCommissionsList: React.FC<SalesCommissionsListProps> = ({
  salesCommissions: initialSalesCommissions,
  isYearly = false,
}) => {
  const { t } = useTranslation();
  const [salesCommissions, setSalesCommissions] = useState<EditableSalesCommission[]>(initialSalesCommissions || []);

  useEffect(() => {
    if (initialSalesCommissions) {
      setSalesCommissions(initialSalesCommissions);
    }
  }, [initialSalesCommissions]);

  return !salesCommissions ? (
    <LoadingSpinner />
  ) : (
    <DataTable
      pad={{ horizontal: '1.5rem' }}
      columns={[
        {
          property: 'name',
          primary: true,
          size: '35%',
          header: <DataTableHeader>{t('common:name')}</DataTableHeader>,
          render: (salesCommission) => (
            <Link to={`/users/${salesCommission.user.id}`}>
              <Text weight={500} color="accent-1">
                {salesCommission.user.name}
              </Text>
            </Link>
          ),
        },
        {
          property: 'percent_rate',
          size: '32%',
          header: <DataTableHeader>{t('commissions:percentage')}</DataTableHeader>,
          render: (salesCommission: EditableSalesCommission) => (
            <DataTableItem boxProps={{ pad: { vertical: '1rem' } }}>
              {salesCommission.isEditing ? (
                <TextInput
                  type="number"
                  value={
                    salesCommission.editedPercentRate !== undefined
                      ? salesCommission.editedPercentRate
                      : salesCommission.percent_rate.toString()
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d*\.?\d*$/.test(value)) {
                      setSalesCommissions((prev) =>
                        prev.map((c) => (c.id === salesCommission.id ? { ...c, editedPercentRate: value } : c))
                      );
                    }
                  }}
                  style={{ width: '80px' }}
                />
              ) : (
                `${salesCommission.percent_rate}%`
              )}
            </DataTableItem>
          ),
        },
        {
          property: 'start_reporting_quarter',
          size: '16%',
          header: <DataTableHeader>{t('commissions:effectiveStartDate')}</DataTableHeader>,
          render: (salesCommission) => (
            <DataTableItem>{getPeriodFromDateString(salesCommission.start_reporting_quarter, isYearly)}</DataTableItem>
          ),
        },
        {
          property: 'end_reporting_quarter',
          size: '16%',
          header: <DataTableHeader>{t('commissions:effectiveEndDate')}</DataTableHeader>,
          render: (salesCommission) => (
            <DataTableItem>
              {salesCommission.end_reporting_quarter === '2999-10-01' ? (
                '—'
              ) : (
                <Text>{getPeriodFromDateString(salesCommission.end_reporting_quarter as string, isYearly)}</Text>
              )}
            </DataTableItem>
          ),
        },
      ]}
      data={salesCommissions}
      background={['light-6', 'white']}
    />
  );
};
